import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { BY_PART } from '../../config/modularized.config';
import { ModelBuilder } from '../../models/builders/model.builder';
import { Model } from '../../models/model';
import { getAvoidErrorHandlingHeader } from '../base/headers';
import { valueOf } from './../../utils/string.utils';

const getModelBody = (model: Model, file: File) => {
  const postBody = new FormData();
  postBody.append('file', file);
  postBody.append('name', model.name);
  postBody.append('order', valueOf(model.order));
  postBody.append('imagePath', model.imagePath);
  postBody.append('active', valueOf(model.active));
  postBody.append('defaultTaskDeviation', valueOf(model.defaultTaskDeviation));
  postBody.append('ip', model.ip);
  postBody.append('reference', valueOf(model.reference));
  postBody.append('ftpUser', model.ftpUser);
  postBody.append('ftpPassword', model.ftpPassword);
  postBody.append('serverPath', model.serverPath);
  postBody.append('logPath', model.logPath);
  postBody.append('alarmPath', model.alarmPath);
  postBody.append('programPath', model.programPath);
  postBody.append('cycle', valueOf(model.cycle));
  postBody.append('controlHash', valueOf(model.control));
  postBody.append('brandHash', valueOf(model.brand));
  postBody.append('adapterHash', valueOf(model.adapter));
  postBody.append('correctionFactor', valueOf(model.correctionFactor));
  postBody.append('defaultToolTime', valueOf(model.defaultToolTime));
  postBody.append('defaultPieceTime', valueOf(model.defaultPieceTime));
  postBody.append('timeCorrector', valueOf(model.timeCorrector));
  postBody.append('readMode', valueOf(model.readMode));
  postBody.append('workingModelGroupHash', valueOf(model.workingModelGroup));
  postBody.append('workingModelGroupSectionHash', valueOf(model.section));
  appendByPartsParameters(postBody, model);
  appendPlannerAttributes(postBody, model);

  return postBody;
};

/**
 * Planner-engine attributes added to Model. Only included when the field is set
 * so the controller stays backwards-compatible with old callers that don't know
 * about these. `diameterRanges` ships as a JSON string in one form field because
 * multipart can't carry a structured list as a @RequestParam.
 */
function appendPlannerAttributes(postBody: FormData, model: Model) {
  if (model.feederType != null && model.feederType !== '') {
    postBody.append('feederType', valueOf(model.feederType));
  }
  if (model.maxPartLengthMm != null) {
    postBody.append('maxPartLengthMm', valueOf(model.maxPartLengthMm));
  }
  if (model.supportsRearEjection != null) {
    postBody.append('supportsRearEjection', valueOf(model.supportsRearEjection));
  }
  // Siempre, incluido el array vacio: es como la ficha vacia la asignacion.
  const theoreticalMachineHashes = Array.isArray(model.theoreticalMachines)
    ? model.theoreticalMachines.map(machine => machine.hash).filter(hash => !!hash)
    : [];
  postBody.append('theoreticalMachineHashesJson', JSON.stringify(theoreticalMachineHashes));
  if (Array.isArray(model.diameterRanges) && model.diameterRanges.length > 0) {
    const payload = model.diameterRanges.map(r => ({
      label: r.label || null,
      minDiameterMm: r.minDiameterMm,
      maxDiameterMm: r.maxDiameterMm,
    }));
    postBody.append('diameterRangesJson', JSON.stringify(payload));
  } else {
    // Send an empty array explicitly so the backend can wipe existing rows on edit.
    postBody.append('diameterRangesJson', '[]');
  }
}

@Injectable()
export class ModelApiService {
  private apiUrl: string;

  constructor(private httpClient: HttpClient) {
    this.apiUrl = `${environment.apiUrl}models`;
  }

  createModel(model: Model, file: File) {
    return this.httpClient.post(this.apiUrl, getModelBody(model, file)).pipe(map(ModelBuilder.fromJson));
  }

  deleteModel(model: Model) {
    return this.httpClient.delete(`${this.apiUrl}/${model.hash}`);
  }

  getActiveModels() {
    return this.fetchActiveModels();
  }

  getActiveModelsSilent() {
    return this.fetchActiveModels(getAvoidErrorHandlingHeader());
  }

  getModel(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}`)
      .pipe(
        map(ModelBuilder.fromJson)
      );
  }

  getModels() {
    return this.httpClient.get(`${this.apiUrl}`).pipe(map(ModelBuilder.fromList));
  }

  getModelsByGroup(hash: string) {
    const params = new HttpParams()
      .append('hash', hash);

    return this.httpClient.get(`${this.apiUrl}/group`, { params }).pipe(map(ModelBuilder.fromList));
  }

  updateModel(model: Model, file: File) {
    return this.httpClient.put(`${this.apiUrl}/${model.hash}`, getModelBody(model, file));
  }

  private fetchActiveModels(headers?: HttpHeaders) {
    return this.httpClient.get(`${this.apiUrl}/active`, { headers }).pipe(map(ModelBuilder.activeModelsFromList));
  }
}
function appendByPartsParameters(postBody: FormData, model: Model) {
  if (environment.menu === BY_PART) {
    postBody.append('numberOfPaths', valueOf(model.numberOfPaths));
    if (model.partsCounterPath != null) {
      postBody.append('partsCounterPath', valueOf(model.partsCounterPath));
    }
    if (model.programReadLimit != null) {
      postBody.append('programReadLimit', valueOf(model.programReadLimit));
    }
    if (model.continuousProductionVariable != null) {
      postBody.append('continuousProductionVariable', valueOf(model.continuousProductionVariable));
    }
    if (model.continuousProductionVariablePath != null) {
      postBody.append('continuousProductionVariablePath', valueOf(model.continuousProductionVariablePath));
    }
    if (model.continuousProductionVariableReversed) {
      postBody.append('continuousProductionVariableReversed', valueOf(1));
    }
    if (model.continuousProductionVariableReversed != null && !model.continuousProductionVariableReversed) {
      postBody.append('continuousProductionVariableReversed', valueOf(0));
    }
    if (model.pauseMinTime != null) {
      postBody.append('pauseMinTime', valueOf(model.pauseMinTime));
    }
    if (model.subprogramCodeLimitToSend != null) {
      postBody.append('subprogramCodeLimitToSend', valueOf(model.subprogramCodeLimitToSend));
    }
  }
}




// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/core/services/api/model-api.service.ts